const CONFIGURATION_ERRORS: Record<string, string> = {
  dose_request_decision_required:
    "The patient's monthly check-in asked to change their dose. Answer it (change, keep with a reason, or a visit first) before approving.",
  dose_request_reason_required:
    "Choose a reason for keeping the current dose or asking for a visit (and write it out for \"Other\").",
  dose_request_tier_mismatch:
    "The dose you chose doesn't match the medication selected for this renewal. Choose the dose again.",
  dose_request_same_dose:
    "Choose a strength other than the current dose, or keep the current dose with a reason.",
  dose_request_keep_changed_dose:
    "You chose to keep the current dose, but a different medication is selected. Switch back to the current dose.",
  dose_request_same_directions:
    "Those are the current directions. Choose different directions, or keep the current directions with a reason.",
  dose_request_exceeds_container:
    "Those directions use more than one vial per 28 days. Choose directions within the vial.",
  identity_verification_required:
    "An ID must be on the patient’s profile before treatment review. Ask the patient to upload an ID or the requested correction, then verify identity during prescribing. Appointments remain available.",
  authorization_confirmation_required:
    "Review the prescription authorization summary and explicitly confirm the initial fill and additional refills before signing.",
  authorization_refills_exceeded:
    "The selected refill count exceeds the current policy. Refresh the chart and select the allowed count or fewer before signing.",
  authorization_supply_unknown:
    "The days of supply could not be confirmed. Ask operations to correct the medication supply configuration before signing; do not infer supply from billing.",
  authorization_supply_exceeds_policy:
    "A full shipment exceeds the permitted authorization period. Ask operations to review the supply configuration before signing.",
  saved_authorization_mismatch:
    "This prescription authorization is already saved. Refresh to retry the immutable saved authorization; a retry cannot change its refills.",
  authorization_save_failed:
    "The prescription authorization could not be saved. Refresh before retrying; do not assume the authorization was recorded.",
  saved_prescription_requires_reconciliation:
    "The linked Healthie prescription could not be verified for this renewal. Operations must reconcile the saved approval and exact prescription before submitting. Do not create another prescription or mark this patient Missing Info.",
  prescription_write_needs_reconciliation:
    "The Healthie write is processing or unconfirmed. No second prescription was created. Refresh shortly; if this persists, ask operations to reconcile the write. Do not mark the patient Missing Info.",
  prescription_write_conflict:
    "The saved prescribing action does not match the recovered Healthie record. Operations must reconcile it before retrying.",
  prescription_write_unavailable:
    "The prescribing action could not be saved. No prescription was sent. Please retry, or contact operations if this persists.",
  pharmacy_submission_incomplete:
    "The chart prescription is saved, but pharmacy submission has not completed. Refresh its status, then use Retry pharmacy submission if it is still pending. The saved note and medication will be reused.",
  saved_prescription_mismatch:
    "This prescription is already saved. Refresh to retry its saved medication and directions; a retry cannot change them.",
  saved_healthie_medication_missing:
    "The saved chart medication is no longer available. Ask the prescriber to review the chart before submitting to the pharmacy.",
  prescription_not_retryable:
    "This order is no longer awaiting pharmacy submission. Refresh to see its current status.",
  physician_form_product_not_mapped:
    "The shared Healthie Physician Approval Note is missing this treatment in its required Peptide Request options. Ask operations to update that form, then retry. This is not missing patient information—do not request a new intake or mark the patient Missing Info. No physician note or prescription was created by this attempt. [physician_form_product_not_mapped]",
  physician_form_mapping_incomplete:
    "The shared Healthie Physician Approval Note is missing a required field. Ask operations to repair the form configuration, then retry. This is not missing patient information. [physician_form_mapping_incomplete]",
  physician_form_options_incomplete:
    "The shared Healthie Physician Approval Note is missing a required provider or decision option. Ask operations to repair the form configuration, then retry. This is not missing patient information. [physician_form_options_incomplete]",
};

// Prescriber refusals whose server message names the exact reason (the
// patient's state, the missing profile field, the Healthie form option).
const PRESCRIBER_ERRORS = new Set([
  "prescriber_not_licensed_in_state",
  "prescriber_identity_required",
  "prescriber_identity_invalid",
  "prescriber_license_expired",
  "physician_form_provider_missing",
]);

export function prescriptionDecisionError(
  error: unknown,
  decision: "approve" | "reject" | "missing_info",
): string {
  const code =
    error && typeof error === "object" && "code" in error ? error.code : null;
  if (typeof code === "string" && Object.hasOwn(CONFIGURATION_ERRORS, code)) {
    return CONFIGURATION_ERRORS[code];
  }
  const message =
    error && typeof error === "object" && "message" in error
      ? error.message
      : null;
  if (
    typeof code === "string" &&
    PRESCRIBER_ERRORS.has(code) &&
    typeof message === "string" &&
    message
  ) {
    return `${message} [${code}]`;
  }
  return decision === "approve"
    ? "The combined approval did not finish. The modal is still open so you can correct the medication choice and retry; completed steps will be resumed safely."
    : decision === "reject"
      ? "The rejection workflow did not finish. Retry to safely resume the locked note, cancellation, refund, and patient message."
      : "The missing-information request could not be created.";
}
